// features/products/pages/ProductImagesPage.tsx
// ENHANCED - Proper Error Handling & Celebrations

import { useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import {
    getProduct, getProductImages, uploadProductImage, addProductImage,
    updateProductImage, deleteProductImage, reorderProductImages,
    setPrimaryProductImage, ProductData, ProductImageData,
} from '../services/productsApi';
import {
    ArrowLeft, Upload, Image, Star, Trash2, Pencil,
    CheckCircle, X, Plus, GripVertical, CloudUpload, PartyPopper,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { extractApiError } from '@/lib/api-error';

function extractErrorMessage(err: any): string {
    const apiError = err?.response?.data?.error;
    if (!apiError) return err?.message || 'An unexpected error occurred.';
    if (apiError.details && Array.isArray(apiError.details) && apiError.details.length > 0) {
        return apiError.details.map((d: any) => d.message).join('. ');
    }
    return apiError.message || 'An unexpected error occurred.';
}

export function ProductImagesPage() {
    const { productId } = useParams<{ productId: string }>();
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [urlInput, setUrlInput] = useState('');
    const [editingAlt, setEditingAlt] = useState<string | null>(null);
    const [altText, setAltText] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const [dragItem, setDragItem] = useState<number | null>(null);
    const [dragOverItem, setDragOverItem] = useState<number | null>(null);

    const { data: product, isLoading } = useQuery({
        queryKey: ['product', productId],
        queryFn: () => getProduct(user!.tenantId, productId!),
        enabled: !!user?.tenantId && !!productId,
    });

    const { data: images, refetch: refetchImages } = useQuery({
        queryKey: ['product-images', productId],
        queryFn: () => getProductImages(user!.tenantId, productId!),
        enabled: !!user?.tenantId && !!productId,
    });

    const deleteMutation = useMutation({
        mutationFn: (imageId: string) => deleteProductImage(user!.tenantId, productId!, imageId),
        onSuccess: () => { refetchImages(); setDeleteConfirm(null); queryClient.invalidateQueries({ queryKey: ['product', productId] }); addToast({ type: 'success', title: 'Image Deleted', message: 'Image has been removed.' }); },
        onError: (err: any) => {
            const { title, message } = extractApiError(err, 'Delete Failed');
            addToast({ type: 'error', title, message, duration: 10000 });
        },
    });

    const setPrimaryMutation = useMutation({
        mutationFn: (imageId: string) => setPrimaryProductImage(user!.tenantId, productId!, imageId),
        onSuccess: () => { refetchImages(); queryClient.invalidateQueries({ queryKey: ['product', productId] }); addToast({ type: 'success', title: 'Primary Image Set ⭐', message: 'This image is now the primary.' }); },
        onError: (err: any) => {
            const { title, message } = extractApiError(err, 'Set Primary Failed');
            addToast({ type: 'error', title, message, duration: 10000 });
        },
    });

    const updateAltMutation = useMutation({
        mutationFn: ({ imageId, alt }: { imageId: string; alt: string }) => updateProductImage(user!.tenantId, productId!, imageId, { alt }),
        onSuccess: () => { setEditingAlt(null); refetchImages(); addToast({ type: 'success', title: 'Alt Text Updated' }); },
        onError: (err: any) => {
            const { title, message } = extractApiError(err, 'Update Failed');
            addToast({ type: 'error', title, message, duration: 10000 });
        },
    });

    const addUrlMutation = useMutation({
        mutationFn: (url: string) => addProductImage(user!.tenantId, productId!, { url, alt: product?.name || '' }),
        onSuccess: () => { setUrlInput(''); refetchImages(); queryClient.invalidateQueries({ queryKey: ['product', productId] }); addToast({ type: 'success', title: 'Image Added 📷', message: 'Image URL has been added.' }); },
        onError: (err: any) => {
            const { title, message } = extractApiError(err, 'Add Failed');
            addToast({ type: 'error', title, message, duration: 10000 });
        },
    });

    const handleFileUpload = async (files: FileList) => {
        setIsUploading(true);
        let successCount = 0;
        for (const file of Array.from(files)) {
            try { await uploadProductImage(user!.tenantId, productId!, file, { alt: product?.name || file.name }); successCount++; } catch { }
        }
        setIsUploading(false);
        refetchImages();
        queryClient.invalidateQueries({ queryKey: ['product', productId] });
        if (successCount > 0) addToast({ type: 'success', title: 'Upload Complete 📷', message: `${successCount} image(s) uploaded.` });
    };

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault(); setIsDragging(false);
        if (e.dataTransfer.files?.length) handleFileUpload(e.dataTransfer.files);
    }, [productId]);

    const handleDragStart = (index: number) => setDragItem(index);
    const handleDragOver = (e: React.DragEvent, index: number) => { e.preventDefault(); setDragOverItem(index); };
    const handleDragLeave = () => setDragOverItem(null);

    const handleDropReorder = async (index: number) => {
        if (dragItem === null || dragItem === index) { setDragItem(null); setDragOverItem(null); return; }
        const imageList = images || [];
        const reordered = [...imageList];
        const [movedItem] = reordered.splice(dragItem, 1);
        reordered.splice(index, 0, movedItem);
        const ids = reordered.map(img => img.id);
        try { await reorderProductImages(user!.tenantId, productId!, ids); refetchImages(); addToast({ type: 'success', title: 'Images Reordered' }); }
        catch { addToast({ type: 'error', title: 'Reorder Failed' }); }
        setDragItem(null); setDragOverItem(null);
    };

    const handleDragEnd = () => { setDragItem(null); setDragOverItem(null); };
    const imageList = images || [];

    if (isLoading) return <div className="flex justify-center py-20"><Spinner size="xl" /></div>;
    if (!product) return <ErrorState title="Product not found" />;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button onClick={() => navigate(`/products/${productId}`)} className="p-2 rounded-xl hover:bg-slate-100" type="button"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
                            <div className="p-2.5 bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl shadow-lg"><Image className="w-5 h-5 text-white" /></div>
                            <div><h1 className="text-xl font-bold text-slate-900">{product.name}</h1><p className="text-sm text-slate-500">{imageList.length} image{imageList.length !== 1 ? 's' : ''}</p></div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1400px] mx-auto p-6 lg:p-8 space-y-6">
                <div onDrop={handleDrop} onDragOver={e => { e.preventDefault(); setIsDragging(true); }} onDragLeave={() => setIsDragging(false)}
                    className={clsx('border-2 border-dashed rounded-2xl p-8 text-center transition-all cursor-pointer', isDragging ? 'border-emerald-400 bg-emerald-50/50 scale-[1.01]' : 'border-slate-300 hover:border-emerald-400')}
                    onClick={() => fileInputRef.current?.click()}>
                    <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={e => { if (e.target.files?.length) handleFileUpload(e.target.files); e.target.value = ''; }} />
                    <CloudUpload className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <h3 className="text-lg font-bold text-slate-700">Drop images here or click to browse</h3>
                    <p className="text-sm text-slate-400 mt-1">JPG, PNG, WebP, GIF · Max 10MB each</p>
                    {isUploading && <Spinner size="sm" className="mt-4" />}
                </div>

                <div className="flex gap-2">
                    <input type="text" value={urlInput} onChange={e => setUrlInput(e.target.value)} placeholder="Or paste an image URL..." className="flex-1 px-4 py-3 border-2 border-slate-200 rounded-xl text-sm" />
                    <button onClick={() => urlInput && addUrlMutation.mutate(urlInput)} disabled={!urlInput || addUrlMutation.isPending}
                        className="px-5 py-3 bg-white border border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50"><Plus className="w-4 h-4" /></button>
                </div>

                {imageList.length === 0 ? (
                    <EmptyState icon={<Image className="w-16 h-16" />} title="No Images" description="Upload images or add URLs." />
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {imageList.map((image, index) => (
                            <motion.div key={image.id} layout draggable
                                onDragStart={() => handleDragStart(index)} onDragOver={(e) => handleDragOver(e, index)}
                                onDragLeave={handleDragLeave} onDrop={() => handleDropReorder(index)} onDragEnd={handleDragEnd}
                                className={clsx('relative group bg-white rounded-2xl border-2 overflow-hidden transition-all',
                                    dragOverItem === index ? 'border-emerald-400 scale-105 shadow-xl' : 'border-slate-200',
                                    dragItem === index ? 'opacity-50 scale-95' : '', 'cursor-grab active:cursor-grabbing')}>
                                <div className="aspect-square bg-slate-100"><img src={image.url} alt={image.alt || product.name} className="w-full h-full object-cover" /></div>
                                {image.isPrimary && <div className="absolute top-2 left-2 px-2 py-1 bg-amber-500 text-white text-[10px] font-bold rounded-lg flex items-center gap-1 shadow-lg"><Star className="w-3 h-3 fill-white" /> Primary</div>}
                                <div className="absolute top-2 right-2 p-1.5 bg-white/90 backdrop-blur-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"><GripVertical className="w-4 h-4 text-slate-500" /></div>
                                <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                                    {!image.isPrimary && <button onClick={(e) => { e.stopPropagation(); setPrimaryMutation.mutate(image.id); }} className="p-1.5 bg-white rounded-lg text-amber-500 hover:bg-amber-50 shadow-sm" type="button"><Star className="w-3.5 h-3.5" /></button>}
                                    <button onClick={(e) => { e.stopPropagation(); setEditingAlt(image.id); setAltText(image.alt || ''); }} className="p-1.5 bg-white rounded-lg text-blue-500 hover:bg-blue-50 shadow-sm" type="button"><Pencil className="w-3.5 h-3.5" /></button>
                                    <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm(image.id); }} className="p-1.5 bg-white rounded-lg text-rose-500 hover:bg-rose-50 shadow-sm" type="button"><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                                <AnimatePresence>
                                    {editingAlt === image.id && (
                                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                            className="absolute inset-0 bg-white/95 backdrop-blur-sm flex items-center justify-center p-4" onClick={e => e.stopPropagation()}>
                                            <div className="w-full space-y-2">
                                                <input type="text" value={altText} onChange={e => setAltText(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Alt text" autoFocus />
                                                <div className="flex gap-2 justify-center">
                                                    <button onClick={() => updateAltMutation.mutate({ imageId: image.id, alt: altText })} className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-xs font-semibold">Save</button>
                                                    <button onClick={() => setEditingAlt(null)} className="px-3 py-1.5 bg-slate-200 rounded-lg text-xs font-semibold">Cancel</button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>

            <AnimatePresence>
                {deleteConfirm && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center">
                            <Trash2 className="w-12 h-12 text-rose-500 mx-auto mb-3" /><h3 className="text-lg font-bold">Delete Image?</h3><p className="text-sm text-slate-500 mt-2">This cannot be undone.</p>
                            <div className="flex gap-3 mt-6"><button onClick={() => setDeleteConfirm(null)} className="flex-1 px-4 py-2.5 border rounded-xl text-sm font-medium">Cancel</button><button onClick={() => deleteMutation.mutate(deleteConfirm)} className="flex-1 px-4 py-2.5 bg-rose-500 text-white rounded-xl text-sm font-semibold">Delete</button></div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}